/**
 * 人物与组织(自带包 `xuwo.people`):住通用库的两个类型,地址族 `people:person` /
 * `people:organization`(type-catalog「人」),实体地址仍是
 * `people:<kind>:<工作区>/<uuid>`,行 id 就是原来的 uuid。属性 id 沿用旧人物库数据源的
 * 字段 id,已存视图的筛选、排序与列配置原样有效。
 *
 * 关系类型是人物类型上的关联属性(§6 R1 方案 A):正向列在关系的发起一方(人物),
 * 列出另一方;「被关联」列在目标类型上。列名按读的那一方:旧人物库里关系
 * `from 是 to 的 <name>`,发起一方看到的是 `reverse_name`(没有就是 `name`),
 * 目标一方看到的是 `name`——所以正向列取 `reverse_name ?? name`,被关联列取 `name`;
 * 对称的关系(没有 `reverse_name`)被关联列名加「(反向)」,同源列名不能重。
 * 关系的标注、备注、起止日期与颜色不再有专门的落点(要记就给类型加属性);`hue`
 * 只是小组件给出厂关系配色用的显示提示,不存。
 */

import type { ExtensionEntityPropertyType } from './extension-entity-property';

export const PEOPLE_PACKAGE_ID = 'xuwo.people';
export const PEOPLE_AUTHORITY = 'people';
export const PEOPLE_KINDS = ['person', 'organization'] as const;
export type PeopleKind = (typeof PEOPLE_KINDS)[number];
export const PEOPLE_TYPE_IDS: Readonly<Record<PeopleKind, string>> = {
  person: `${PEOPLE_PACKAGE_ID}/person`,
  organization: `${PEOPLE_PACKAGE_ID}/organization`,
};

type Localized = Readonly<{ zh: string; en: string }>;

/**
 * 两个类型共有的属性(清单词汇)。邮箱、电话、链接用通用库的邮箱、电话、网址类型;
 * 地址与生日是文本(生日可以只有 `MM-DD`,不是日期);头像是只带来源地址的文件。
 */
export const PEOPLE_PROPERTIES: readonly Readonly<{
  key: string;
  name: Localized;
  type: ExtensionEntityPropertyType;
}>[] = [
  { key: 'name', name: { zh: '名称', en: 'Name' }, type: 'text' },
  { key: 'description', name: { zh: '简介', en: 'Description' }, type: 'text' },
  { key: 'email', name: { zh: '邮箱', en: 'Email' }, type: 'email' },
  { key: 'phone', name: { zh: '电话', en: 'Phone' }, type: 'phone' },
  { key: 'url', name: { zh: '链接', en: 'Link' }, type: 'url' },
  { key: 'location', name: { zh: '地址', en: 'Location' }, type: 'text' },
  { key: 'birthday', name: { zh: '生日', en: 'Birthday' }, type: 'text' },
  { key: 'avatar', name: { zh: '头像', en: 'Avatar' }, type: 'file' },
  {
    key: 'createdAt',
    name: { zh: '创建时间', en: 'Created' },
    type: 'created-time',
  },
  {
    key: 'updatedAt',
    name: { zh: '更新时间', en: 'Updated' },
    type: 'updated-time',
  },
];

export type PeopleRelationDomain = 'person-person' | 'person-organization';

/**
 * 出厂的关系:旧人物库的七个出厂关系类型(`builtin-*`),各成一对属性——正向列在
 * 人物类型上,被关联列在目标类型上(人物—人物在人物类型,人物—组织在组织类型)。
 */
export const PEOPLE_RELATIONS: readonly Readonly<{
  /** 旧人物库的关系类型 id。 */
  typeId: string;
  domain: PeopleRelationDomain;
  key: string;
  name: Localized;
  inverseKey: string;
  inverseName: Localized;
  hue: string;
}>[] = [
  {
    typeId: 'builtin-friend',
    domain: 'person-person',
    key: 'friends',
    name: { zh: '朋友', en: 'Friend' },
    inverseKey: 'friendsInverse',
    inverseName: { zh: '朋友(反向)', en: 'Friend (reverse)' },
    hue: 'green',
  },
  {
    typeId: 'builtin-family',
    domain: 'person-person',
    key: 'family',
    name: { zh: '家人', en: 'Family' },
    inverseKey: 'familyInverse',
    inverseName: { zh: '家人(反向)', en: 'Family (reverse)' },
    hue: 'orange',
  },
  {
    typeId: 'builtin-colleague',
    domain: 'person-person',
    key: 'colleagues',
    name: { zh: '同事', en: 'Colleague' },
    inverseKey: 'colleaguesInverse',
    inverseName: { zh: '同事(反向)', en: 'Colleague (reverse)' },
    hue: 'blue',
  },
  {
    typeId: 'builtin-mentor',
    domain: 'person-person',
    key: 'mentees',
    name: { zh: '学生', en: 'Mentee' },
    inverseKey: 'mentors',
    inverseName: { zh: '导师', en: 'Mentor' },
    hue: 'purple',
  },
  {
    typeId: 'builtin-collaborator',
    domain: 'person-person',
    key: 'collaborators',
    name: { zh: '合作者', en: 'Collaborator' },
    inverseKey: 'collaboratorsInverse',
    inverseName: { zh: '合作者(反向)', en: 'Collaborator (reverse)' },
    hue: 'yellow',
  },
  {
    typeId: 'builtin-employment',
    domain: 'person-organization',
    key: 'employers',
    name: { zh: '任职', en: 'Employer' },
    inverseKey: 'members',
    inverseName: { zh: '成员', en: 'Member' },
    hue: 'brown',
  },
  {
    typeId: 'builtin-education',
    domain: 'person-organization',
    key: 'almaMaters',
    name: { zh: '就读', en: 'Alma mater' },
    inverseKey: 'alumni',
    inverseName: { zh: '校友', en: 'Alumnus' },
    hue: 'gray',
  },
];

/** 任职:人物页名片上的「职位 @ 组织」取这一列的第一个组织。 */
export const PEOPLE_EMPLOYMENT_PROPERTY = 'employers';

/** 关系的显示色(出厂关系才有;用户自己加的关联属性是灰)。 */
export function peopleRelationHue(propertyId: string): string {
  return (
    PEOPLE_RELATIONS.find(
      relation =>
        relation.key === propertyId || relation.inverseKey === propertyId
    )?.hue ?? 'gray'
  );
}

/** 自带包清单 `entityTypes[]` 里的两个类型(通用库存储,带地址族)。 */
export function peopleManifestEntityTypes() {
  const scalar = PEOPLE_PROPERTIES.map(property => ({
    key: property.key,
    name: property.name,
    type: property.type,
  }));
  const targetOf = (domain: PeopleRelationDomain) =>
    domain === 'person-person'
      ? PEOPLE_TYPE_IDS.person
      : PEOPLE_TYPE_IDS.organization;
  const inverse = (relation: (typeof PEOPLE_RELATIONS)[number]) => ({
    key: relation.inverseKey,
    name: relation.inverseName,
    type: 'relation-inverse' as const,
    config: {
      fromTypeId: PEOPLE_TYPE_IDS.person,
      fromPropertyId: relation.key,
    },
  });
  return [
    {
      key: 'person',
      label: { zh: '人物', en: 'Person' },
      storage: {
        kind: 'general' as const,
        ref: { authority: PEOPLE_AUTHORITY, kind: 'person' },
        properties: [
          ...scalar,
          ...PEOPLE_RELATIONS.map(relation => ({
            key: relation.key,
            name: relation.name,
            type: 'relation' as const,
            config: { targetTypeId: targetOf(relation.domain), multiple: true },
          })),
          ...PEOPLE_RELATIONS.filter(
            relation => relation.domain === 'person-person'
          ).map(inverse),
        ],
      },
    },
    {
      key: 'organization',
      label: { zh: '组织', en: 'Organization' },
      storage: {
        kind: 'general' as const,
        ref: { authority: PEOPLE_AUTHORITY, kind: 'organization' },
        properties: [
          ...scalar,
          ...PEOPLE_RELATIONS.filter(
            relation => relation.domain === 'person-organization'
          ).map(inverse),
        ],
      },
    },
  ];
}
